import { useState, useEffect, useMemo } from 'react';
import supabase from '../supabase';
import { theme, font, inputStyle, labelStyle, pillButton, outlineButton } from './adminTheme';
import { buildEmailHtml, buildNotificationHtml, LOGO_OPTIONS, SENDER_NAME_OPTIONS, CITY_PRESETS, TIMEZONE_OPTIONS } from './emailTemplates';
import { useAdminLang } from './AdminLangContext';
import '../design-system.css';

// Défini localement (plutôt qu'importé d'adminTheme.js) pour que ce fichier
// reste indépendant de la version d'adminTheme.js actuellement déployée —
// certains déploiements n'ont pas encore l'export `chipButton`.
const chipButton = {
    padding: '6px 12px',
    background: 'none',
    border: `1px solid ${theme.border}`,
    color: theme.text,
    borderRadius: 0,
    fontSize: '11px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: font,
};

const T = {
    fr: {
        tabs: { compose: 'Composer', subscribers: 'Abonnés', history: 'Historique' },
        subscribersActive: (n) => `${n} abonné${n !== 1 ? 's' : ''} actif${n !== 1 ? 's' : ''}`,
        senderName: "Nom de l'expéditeur",
        logo: 'Logo',
        french: 'Français',
        titleFrPh: 'Titre (FR)',
        messageFrPh: 'Message (FR)',
        english: 'English',
        titleEnPh: 'Title (EN)',
        messageEnPh: 'Message (EN)',
        vietnamese: 'Tiếng Việt',
        optional: '(optionnel)',
        titleViPh: 'Tiêu đề (VI)',
        messageViPh: 'Thông điệp (VI)',
        buttonText: 'Bouton (texte)',
        buttonLink: 'Bouton (lien)',
        includeFooterImage: 'Inclure une image en pied de page',
        footerImageUrl: "URL de l'image",
        citiesFooter: 'Villes affichées dans le footer',
        timezone: 'Fuseau horaire',
        testSend: 'Envoi de test',
        testEmailPh: 'ton-email@gmail.com',
        sendTest: 'Envoyer test',
        sending: 'Envoi...',
        sendTo: (n) => `Envoyer à ${n} abonnés`,
        livePreview: 'Aperçu en direct',
        needMessage: 'Au moins un message (FR ou EN) est requis.',
        needTestEmail: 'Entre un email de test.',
        confirmSend: (n) => `Envoyer à ${n} abonné(s) ?`,
        testSent: (email) => `Test envoyé à ${email}`,
        sentTo: (n) => `Envoyé à ${n} abonné(s)`,
        failed: (msg) => `Échec — ${msg}`,
        activeTotal: (active, total) => `${active} actif${active !== 1 ? 's' : ''} · ${total} total`,
        searchEmail: 'Rechercher un email...',
        loading: 'Chargement...',
        noSubscribers: 'Aucun abonné.',
        unsubscribed: 'Désabonné',
        active: 'Actif',
        remove: 'Retirer',
        confirmRemove: 'Retirer cet abonné ?',
        backToHistory: "← Retour à l'historique",
        recipients: (n) => `${n} destinataire${n !== 1 ? 's' : ''}`,
        detailsUnavailable: 'Détails indisponibles pour cet envoi.',
        noSends: 'Aucun envoi.',
        test: 'TEST',
        notification: 'NOTIFICATION',
        notif: '· NOTIF',
    },
    en: {
        tabs: { compose: 'Compose', subscribers: 'Subscribers', history: 'History' },
        subscribersActive: (n) => `${n} active subscriber${n !== 1 ? 's' : ''}`,
        senderName: 'Sender name',
        logo: 'Logo',
        french: 'Français',
        titleFrPh: 'Title (FR)',
        messageFrPh: 'Message (FR)',
        english: 'English',
        titleEnPh: 'Title (EN)',
        messageEnPh: 'Message (EN)',
        vietnamese: 'Tiếng Việt',
        optional: '(optional)',
        titleViPh: 'Tiêu đề (VI)',
        messageViPh: 'Thông điệp (VI)',
        buttonText: 'Button (text)',
        buttonLink: 'Button (link)',
        includeFooterImage: 'Include an image in the footer',
        footerImageUrl: 'Image URL',
        citiesFooter: 'Cities shown in the footer',
        timezone: 'Timezone',
        testSend: 'Test send',
        testEmailPh: 'your-email@gmail.com',
        sendTest: 'Send test',
        sending: 'Sending...',
        sendTo: (n) => `Send to ${n} subscribers`,
        livePreview: 'Live preview',
        needMessage: 'At least one message (FR or EN) is required.',
        needTestEmail: 'Enter a test email.',
        confirmSend: (n) => `Send to ${n} subscriber(s)?`,
        testSent: (email) => `Test sent to ${email}`,
        sentTo: (n) => `Sent to ${n} subscriber(s)`,
        failed: (msg) => `Failed — ${msg}`,
        activeTotal: (active, total) => `${active} active · ${total} total`,
        searchEmail: 'Search an email...',
        loading: 'Loading...',
        noSubscribers: 'No subscribers.',
        unsubscribed: 'Unsubscribed',
        active: 'Active',
        remove: 'Remove',
        confirmRemove: 'Remove this subscriber?',
        backToHistory: '← Back to history',
        recipients: (n) => `${n} recipient${n !== 1 ? 's' : ''}`,
        detailsUnavailable: 'Details unavailable for this send.',
        noSends: 'No sends.',
        test: 'TEST',
        notification: 'NOTIFICATION',
        notif: '· NOTIF',
    },
};

const EMPTY_FORM = {
    titleFr: '', titleEn: '', titleVi: '',
    messageFr: '', messageEn: '', messageVi: '',
    ctaLabel: 'Explorer', ctaUrl: '',
    showOgFooter: false, ogImageUrl: '',
    testEmail: '',
    showDateline: true,
    datelineCities: ['Montréal, Canada'],
    timezone: TIMEZONE_OPTIONS[0].tz,
    datelineAlign: 'center',
    senderName: 'winston.ie by Longden Collective',
    logoUrl: LOGO_OPTIONS[0].url,
};

function AdminNewsletter() {
    const { lang } = useAdminLang();
    const t = T[lang] || T.fr;
    const [subTab, setSubTab] = useState('compose');

    return (
        <div>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '28px', borderBottom: `1px solid ${theme.border}` }}>
                {[
                    { id: 'compose', label: t.tabs.compose },
                    { id: 'subscribers', label: t.tabs.subscribers },
                    { id: 'history', label: t.tabs.history },
                ].map(tab => (
                    <button key={tab.id} onClick={() => setSubTab(tab.id)} style={{ background: 'none', border: 'none', borderBottom: subTab === tab.id ? `2px solid ${theme.text}` : '2px solid transparent', color: subTab === tab.id ? theme.text : theme.textMuted, fontSize: '12.5px', fontWeight: subTab === tab.id ? 700 : 500, fontFamily: font, cursor: 'pointer', padding: '0 0 10px' }}>
                        {tab.label}
                    </button>
                ))}
            </div>

            {subTab === 'compose' && <ComposeSection t={t} />}
            {subTab === 'subscribers' && <SubscribersSection t={t} />}
            {subTab === 'history' && <HistorySection t={t} />}
        </div>
    );
}

function ComposeSection({ t }) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [subscriberCount, setSubscriberCount] = useState(0);
    const [sending, setSending] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');

    useEffect(() => {
        const fetchCount = async () => {
            const { count } = await supabase.from('newsletter_subscribers').select('*', { count: 'exact', head: true }).eq('unsubscribed', false).eq('confirmed', true);
            setSubscriberCount(count || 0);
        };
        fetchCount();
    }, []);

    const previewHtml = useMemo(() => buildEmailHtml(form), [form]);
    const update = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

    const toggleCity = (city) => {
        setForm(f => ({
            ...f,
            datelineCities: f.datelineCities.includes(city) ? f.datelineCities.filter(c => c !== city) : [...f.datelineCities, city],
        }));
    };

    const handleSend = async (asTest) => {
        if (!form.messageFr.trim() && !form.messageEn.trim()) { setStatusMsg(t.needMessage); return; }
        if (asTest && !form.testEmail.trim()) { setStatusMsg(t.needTestEmail); return; }
        if (!asTest && !window.confirm(t.confirmSend(subscriberCount))) return;

        setSending(true);
        setStatusMsg('');

        try {
            const { data, error } = await supabase.functions.invoke('send-newsletter', {
                body: { ...form, testEmail: asTest ? form.testEmail : undefined },
            });
            if (error) throw error;
            setStatusMsg(asTest ? t.testSent(form.testEmail) : t.sentTo(data?.sent || subscriberCount));
            if (!asTest) setForm(EMPTY_FORM);
        } catch (err) {
            setStatusMsg(t.failed(err.message || err));
        } finally {
            setSending(false);
        }
    };

    return (
        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 380px', minWidth: '320px' }}>
                <p style={{ fontFamily: font, fontSize: '11px', color: theme.textMuted, margin: '0 0 20px' }}>
                    {t.subscribersActive(subscriberCount)}
                </p>

                <p style={{ ...labelStyle, marginTop: 0 }}>{t.senderName}</p>
                <select value={form.senderName} onChange={e => setForm(f => ({ ...f, senderName: e.target.value }))} style={{ ...inputStyle, marginBottom: '20px' }}>
                    {SENDER_NAME_OPTIONS().map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>

                <p style={labelStyle}>{t.logo}</p>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                    {LOGO_OPTIONS.map(opt => (
                        <div key={opt.url} onClick={() => setForm(f => ({ ...f, logoUrl: opt.url }))} style={{ flex: 1, padding: '10px', border: `1px solid ${form.logoUrl === opt.url ? theme.text : theme.border}`, borderRadius: 0, cursor: 'pointer', textAlign: 'center' }}>
                            <img src={opt.url} alt={opt.label} style={{ height: '24px', margin: '0 auto 6px', display: 'block' }} />
                            <p style={{ fontFamily: font, fontSize: '9.5px', color: theme.textMuted, margin: 0 }}>{opt.label}</p>
                        </div>
                    ))}
                </div>

                <p style={labelStyle}>{t.french}</p>
                <input value={form.titleFr} onChange={update('titleFr')} style={{ ...inputStyle, marginBottom: '10px' }} placeholder={t.titleFrPh} />
                <textarea rows={3} value={form.messageFr} onChange={update('messageFr')} style={{ ...inputStyle, marginBottom: '20px', resize: 'vertical' }} placeholder={t.messageFrPh} />

                <p style={labelStyle}>{t.english}</p>
                <input value={form.titleEn} onChange={update('titleEn')} style={{ ...inputStyle, marginBottom: '10px' }} placeholder={t.titleEnPh} />
                <textarea rows={3} value={form.messageEn} onChange={update('messageEn')} style={{ ...inputStyle, marginBottom: '20px', resize: 'vertical' }} placeholder={t.messageEnPh} />

                <p style={labelStyle}>{t.vietnamese} <span style={{ color: theme.textFaint }}>{t.optional}</span></p>
                <input value={form.titleVi} onChange={update('titleVi')} style={{ ...inputStyle, marginBottom: '10px' }} placeholder={t.titleViPh} />
                <textarea rows={2} value={form.messageVi} onChange={update('messageVi')} style={{ ...inputStyle, marginBottom: '20px', resize: 'vertical' }} placeholder={t.messageViPh} />

                <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                    <div style={{ flex: 1 }}>
                        <label style={labelStyle}>{t.buttonText}</label>
                        <input value={form.ctaLabel} onChange={update('ctaLabel')} style={inputStyle} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={labelStyle}>{t.buttonLink}</label>
                        <input value={form.ctaUrl} onChange={update('ctaUrl')} style={inputStyle} placeholder="/campaign/2026" />
                    </div>
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontFamily: font, fontSize: '12px', color: theme.text }}>
                    <input type="checkbox" checked={form.showOgFooter} onChange={e => setForm(f => ({ ...f, showOgFooter: e.target.checked }))} />
                    {t.includeFooterImage}
                </label>
                {form.showOgFooter && (
                    <input value={form.ogImageUrl} onChange={update('ogImageUrl')} style={{ ...inputStyle, marginBottom: '20px' }} placeholder={t.footerImageUrl} />
                )}

                <div style={{ height: '1px', background: theme.border, margin: '20px 0' }} />

                <p style={labelStyle}>{t.citiesFooter}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
                    {CITY_PRESETS.map(city => (
                        <button key={city} onClick={() => toggleCity(city)} style={{ ...chipButton, background: form.datelineCities.includes(city) ? theme.text : 'none', color: form.datelineCities.includes(city) ? '#fff' : theme.text, fontSize: '10.5px' }}>
                            {city}
                        </button>
                    ))}
                </div>

                <p style={labelStyle}>{t.timezone}</p>
                <select value={form.timezone} onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))} style={{ ...inputStyle, marginBottom: '20px' }}>
                    {TIMEZONE_OPTIONS.map(opt => <option key={opt.tz} value={opt.tz}>{opt.label}</option>)}
                </select>

                <div style={{ height: '1px', background: theme.border, margin: '20px 0' }} />

                <label style={labelStyle}>{t.testSend}</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                    <input value={form.testEmail} onChange={update('testEmail')} style={inputStyle} placeholder={t.testEmailPh} />
                    <button className="wlink" onClick={() => handleSend(true)} disabled={sending} style={{ ...outlineButton, whiteSpace: 'nowrap' }}>{t.sendTest}</button>
                </div>

                <button className="wlink" onClick={() => handleSend(false)} disabled={sending} style={{ ...pillButton, width: '100%' }}>
                    {sending ? t.sending : t.sendTo(subscriberCount)}
                </button>

                {statusMsg && <p style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font, margin: '14px 0 0', lineHeight: 1.5 }}>{statusMsg}</p>}
            </div>

            <div style={{ flex: '1 1 400px', minWidth: '320px' }}>
                <p style={labelStyle}>{t.livePreview}</p>
                <div style={{ border: `1px solid ${theme.border}`, borderRadius: 0, overflow: 'hidden', background: '#fff' }}>
                    <iframe title="preview" srcDoc={previewHtml} style={{ width: '100%', height: '640px', border: 'none' }} />
                </div>
            </div>
        </div>
    );
}

function SubscribersSection({ t }) {
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const fetchSubscribers = async () => {
        setLoading(true);
        const { data, error } = await supabase.from('newsletter_subscribers').select('*').order('subscribed_at', { ascending: false });
        if (!error && data) setSubscribers(data);
        setLoading(false);
    };

    useEffect(() => { fetchSubscribers(); }, []);

    const filtered = useMemo(() => {
        if (!search.trim()) return subscribers;
        const q = search.toLowerCase();
        return subscribers.filter(s => s.email.toLowerCase().includes(q));
    }, [subscribers, search]);

    const handleRemove = async (id) => {
        if (!window.confirm(t.confirmRemove)) return;
        await supabase.from('newsletter_subscribers').delete().eq('id', id);
        fetchSubscribers();
    };

    const activeCount = subscribers.filter(s => !s.unsubscribed && s.confirmed).length;

    return (
        <div>
            <p style={{ fontFamily: font, fontSize: '11px', color: theme.textMuted, margin: '0 0 16px' }}>
                {t.activeTotal(activeCount, subscribers.length)}
            </p>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t.searchEmail} style={{ ...inputStyle, maxWidth: '320px', marginBottom: '20px' }} />

            {loading ? (
                <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.loading}</p>
            ) : filtered.length === 0 ? (
                <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.noSubscribers}</p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxWidth: '560px' }}>
                    {filtered.map(s => (
                        <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: 0 }}>
                            <div>
                                <p style={{ margin: 0, fontFamily: font, fontSize: '12.5px', color: theme.text }}>{s.email}</p>
                                <p style={{ margin: 0, fontFamily: font, fontSize: '9.5px', color: theme.textMuted }}>
                                    {s.unsubscribed ? t.unsubscribed : t.active} · {new Date(s.subscribed_at).toLocaleDateString('fr-CA')}
                                </p>
                            </div>
                            <button className="wlink" onClick={() => handleRemove(s.id)} style={{ background: 'none', border: 'none', color: theme.textMuted, fontSize: '11px', fontWeight: 500, cursor: 'pointer', fontFamily: font }}>{t.remove}</button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function HistorySection({ t }) {
    const [sends, setSends] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        const fetchSends = async () => {
            const { data, error } = await supabase.from('newsletter_sends').select('*').order('created_at', { ascending: false });
            if (!error && data) setSends(data);
            setLoading(false);
        };
        fetchSends();
    }, []);

    if (selected) {
        const previewHtml = selected.config
            ? (selected.config.isNotification ? buildNotificationHtml(selected.config) : buildEmailHtml(selected.config))
            : null;
        return (
            <div>
                <button className="wlink" onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: theme.textMuted, fontSize: '11px', fontWeight: 500, cursor: 'pointer', fontFamily: font, marginBottom: '16px' }}>
                    {t.backToHistory}
                </button>
                <p style={{ fontFamily: font, fontSize: '13px', color: theme.text, margin: '0 0 4px' }}>
                    {selected.subject} {selected.is_test && <span style={{ fontSize: '9px', color: theme.textMuted, border: `1px solid ${theme.border}`, borderRadius: 0, padding: '1px 5px', marginLeft: '6px' }}>{t.test}</span>}
                    {selected.config?.isNotification && <span style={{ fontSize: '9px', color: theme.textMuted, border: `1px solid ${theme.border}`, borderRadius: 0, padding: '1px 5px', marginLeft: '6px' }}>{t.notification}</span>}
                </p>
                <p style={{ fontFamily: font, fontSize: '11px', color: theme.textMuted, margin: '0 0 20px' }}>
                    {new Date(selected.created_at).toLocaleString('fr-CA')} · {t.recipients(selected.recipient_count)}
                </p>
                {previewHtml ? (
                    <div style={{ border: `1px solid ${theme.border}`, borderRadius: 0, overflow: 'hidden', background: '#fff', maxWidth: '640px' }}>
                        <iframe title="preview" srcDoc={previewHtml} style={{ width: '100%', height: '640px', border: 'none' }} />
                    </div>
                ) : (
                    <p style={{ fontFamily: font, fontSize: '12px', color: theme.textMuted }}>{t.detailsUnavailable}</p>
                )}
            </div>
        );
    }

    return (
        <div>
            {loading ? (
                <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.loading}</p>
            ) : sends.length === 0 ? (
                <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.noSends}</p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '560px' }}>
                    {sends.map(s => (
                        <button key={s.id} onClick={() => setSelected(s)} style={{ textAlign: 'left', padding: '12px 14px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: 0, cursor: 'pointer', fontFamily: font }}>
                            <p style={{ margin: '0 0 3px', fontSize: '12.5px', color: theme.text }}>
                                {s.subject}
                                {s.is_test && <span style={{ fontSize: '9px', color: theme.textMuted, marginLeft: '6px' }}>{t.test}</span>}
                                {s.config?.isNotification && <span style={{ fontSize: '9px', color: theme.textMuted, marginLeft: '6px' }}>{t.notif}</span>}
                            </p>
                            <p style={{ margin: 0, fontSize: '10px', color: theme.textMuted }}>
                                {new Date(s.created_at).toLocaleDateString('fr-CA')} · {t.recipients(s.recipient_count)}
                            </p>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default AdminNewsletter;